import { z } from 'zod';
import type { ShippingListDriverNotices } from './ShippingDriverNotice';

export const SERVICE_WITH_OPERATOR_AUTOFILL_DESTINATION_ID =
  '0bca3a7c-0df7-462b-9c35-79bac256fe7a';

const isEmptyProductRow = (p: unknown): boolean => {
  const row = p as Partial<{ name: string; code: string; sku: string }>;
  return !row?.name?.trim() && !row?.code?.trim() && !row?.sku?.trim();
};

const isUuid = (value?: string | null): boolean =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value
  );

const requiredUuid = (msg: string) =>
  z
    .string()
    .min(1, msg)
    .refine((val) => isUuid(val), { message: msg });

const MAX_PACKAGES_QUANTITY = 50;

const packagesQuantitySchema = z
  .string()
  .optional()
  .refine((val) => !val || Number(val) >= 1, {
    message: 'La cantidad de bultos debe ser mayor o igual a 1',
  })
  .refine((val) => !val || Number(val) <= MAX_PACKAGES_QUANTITY, {
    message: `La cantidad de bultos no puede ser mayor a ${MAX_PACKAGES_QUANTITY}`,
  });

/**
 * Comercio, conductores, productos y destino de un pedido, tal como los consume
 * ATC para precargar el formulario de creación de casos.
 */
export interface ShippingAtcInfo {
  business: { name: string; prevId: string };
  drivers: { firstName: string; lastName: string; phone: string }[];
  products?: {
    name: string | null;
    sku: string | null;
    code: string | null;
    quantity: number;
  }[];
  destination?: { city: string | null; department: string | null };
  pickup?: ShippingAtcScan & { pickupNumber: string | null } | null;
  driverHistory?: ShippingAtcDriverStep[];
  status?: { id: string; name: string } | null;
  commerceShippingNumber?: string | null;
}

export interface ShippingAtcScan {
  driverName: string;
  phone: string | null;
  event: string;
  at: string;
}

export interface ShippingAtcDriverStep extends ShippingAtcScan {
  role: 'PICKUP' | 'SCAN' | 'ROUTE';
  driverId: string;
  statusName: string | null;
  visitStatus: string | null;
  visitedAt: string | null;
  pickupNumber: string | null;
  routeId: string | null;
  attempt: number | null;
  notDeliveredReason: string | null;
}

export interface ShippingBasicInfo {
  number: number;
  commerceShippingNumber: string;
  recipient: string;
  commerceName: string;
  statusName: string;
  destinationAddress: string;
}

export interface ProductSizeApi {
  id: string;
  externalId?: string;
  name: string;
}

export interface ShippingProductApi {
  id?: string;
  sku?: string;
  code?: string;
  name: string;
  description?: string;
  quantity: number;
  details?: string;
  weight?: string;
  value?: number;
  productSizeId?: string;
  productSize?: ProductSizeApi;
  category?: string | null;
  shippingNumber?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AddressApi {
  id: string;
  streetName: string;
  doorNumber: string;
  apartmentNumber?: string;
  corner?: string;
  zipCode?: string;
  lat?: number;
  long?: number;
  neighborhoodId: string;
}

export interface NestedEntityApi {
  id: string;
  externalId?: string;
  name: string;
  commercialName?: string;
  code?: string;
  claveNegocio?: string;
  tipoNegocio?: string;
  businessKey?: string;
  businessType?: string;
  color?: string;
  businessName?: string;
}

export interface TimeSlotApi {
  id: string;
  code?: string | null;
  dateTimeFrom?: string | null;
  dateTimeTo?: string | null;
}

export interface LogisticOperatorNestedApi extends NestedEntityApi {
  headquarter?: boolean;
  parentId?: string | null;
  parent?: {
    id: string;
    externalId?: string;
    name: string;
    headquarter?: boolean;
  };
}

export interface ShippingZoneApi {
  id: string;
  shippingNumber: string;
  zoneTypeId: string;
  zoneId: string;
  createdAt: string;
  updatedAt: string;
  zone: NestedEntityApi;
  zoneType: NestedEntityApi;
}

export interface ZoneMatch {
  zoneId: string;
  zoneTypeId: string;
  zoneName: string;
  zoneTypeName: string;
  isInsidePolygon: boolean;
  distanceInMeters: number;
}

export interface GeocodeZoneResult {
  zoneId: string | null;
  zoneName: string | null;
  zoneTypeId: string | null;
  zoneTypeName: string | null;
  zones: ZoneMatch[];
  zoneError: boolean;
  zoneErrorDescription: string | null;
}

export interface ShippingHistoryApi {
  id: string;
  shippingNumber: string;
  statusId: string;
  event: string;
  eventDt: string;
  details: string;
  historyLevel: string;
  eventJsonData: {
    status?: {
      externalId: string;
      name: string;
      commercialName?: string;
    };
    shippingNumber?: number;
    details?: string;
    user?: string;
    reasonName?: string;
    reasonDescription?: string;
    reasonDetails?: string;
    destination?: {
      country?: { externalId: string; name: string };
      stateOrDepartment?: { externalId: string; name: string };
      city?: { externalId: string; name: string };
      neighborhood?: { externalId: string; name: string };
      streetName?: string;
      doorNumber?: string;
      apartmentNumber?: string;
      lat?: string;
      long?: string;
    };
    origin?: {
      country?: { externalId: string; name: string };
      stateOrDepartment?: { externalId: string; name: string };
      city?: { externalId: string; name: string };
      neighborhood?: { externalId: string; name: string };
      streetName?: string;
      doorNumber?: string;
      apartmentNumber?: string;
      lat?: string;
      long?: string;
    };
    [key: string]: unknown;
  };
  user: string;
  createdAt: string;
  updatedAt: string;
  status?: {
    name: string;
  };
}

import { ServiceWithOperator } from '../../LogisticOperator/models/ServiceWithOperator';

export interface ShippingListAddress {
  streetName?: string;
  doorNumber?: string;
  apartmentNumber?: string;
}

export interface ShippingListService {
  id: string;
  name?: string;
  commercialName?: string;
  color?: string;
}

export interface ShippingListZone {
  zone?: { id: string; name: string } | null;
  zoneType?: { name: string } | null;
}

/**
 * Fila del listado (`GET /shipping/list`): solo lo que la grilla muestra.
 * Es un subconjunto estructural de ShippingApi, asi que las pantallas que
 * siguen consumiendo la respuesta completa tipan igual contra este tipo.
 */
export interface ShippingListApi {
  number: string;
  commerceShippingNumber?: string;
  recipient?: string;
  createdAt?: string;
  dateToDelivery?: string;
  dateDelivered?: string;
  statusId?: string;
  commerceId?: string;
  logisticOperatorId?: string;
  lastDriverName?: string | null;
  status?: { id: string; name: string; commercialName?: string };
  commerce?: { id: string; name: string };
  originAddress?: ShippingListAddress;
  destinationAddress?: ShippingListAddress;
  commercialService?: ShippingListService;
  serviceWithOperator?: { commercialService?: ShippingListService | null };
  shippingZones?: ShippingListZone[];
  driverNotices?: ShippingListDriverNotices | null;
}

export interface ShippingApi {
  id?: string;
  number: string;
  originAddressId: string;
  destinationAddressId: string;
  logisticOperatorId: string;
  commerceId: string;
  recipient: string;
  recipientPhoneNumber?: string;
  recipientId?: string | null;
  dateToDelivery: string;
  dateDelivered?: string;
  /** Hora de corte de la vuelta, `HH:mm`. Solo en retiros agendados. */
  pickupCycleTime?: string | null;
  commerceShippingNumber: string;
  logisticOperatorShippingNumber: string;
  invoiceNumber?: string;
  commerceReference?: string | null;
  packageQuantity?: number;
  originCountryId: string;
  originCityId: string;
  originStateOrDepartmentId: string;
  originNeighborhoodId: string;
  destinationCountryId: string;
  destinationCityId: string;
  destinationStateOrDepartmentId: string;
  destinationNeighborhoodId: string;
  administrationDetails?: string;
  deliveryDetails?: string;
  pickupDetails: string;
  statusId: string;
  sizeId: string;
  classificationId: string;
  complexityId: string;
  typeId?: string;
  tags?: string;
  channelId: string;
  branchId: string;
  timeSlotId: string;
  commercialServiceId: string;
  serviceWithOperatorId?: string;
  entryDate?: string;
  acceptedDate?: string;
  labeledDate?: string;
  modifiedDate?: string;
  pickedUpDate?: string;
  shippingProducts?: ShippingProductApi[];
  shippingZones?: ShippingZoneApi[];
  vehicleType?: string;
  createdAt?: string;
  updatedAt?: string;
  timeFrom?: string;
  timeTo?: string;

  destinationAddress?: AddressApi;
  destinationCountry?: NestedEntityApi;
  destinationCity?: NestedEntityApi;
  destinationStateOrDepartment?: NestedEntityApi;
  destinationNeighborhood?: NestedEntityApi;
  originAddress?: AddressApi;
  originCountry?: NestedEntityApi;
  originCity?: NestedEntityApi;
  originStateOrDepartment?: NestedEntityApi;
  originNeighborhood?: NestedEntityApi;
  logisticOperator?: LogisticOperatorNestedApi;
  commerce?: NestedEntityApi;
  branch?: NestedEntityApi;
  channel?: NestedEntityApi;
  timeSlot?: TimeSlotApi;
  size?: NestedEntityApi;
  classification?: NestedEntityApi;
  complexity?: NestedEntityApi;
  type?: NestedEntityApi;
  commercialService?: NestedEntityApi;
  serviceWithOperator?: ServiceWithOperator;
  status?: NestedEntityApi;
  locationId?: string | null;
  location?: { id: string; name: string } | null;
  lastDriverName?: string | null;
  labelNumber?: string | null;
  labelProvider?: string | null;
  geocoderError?: boolean;
  geocoderErrorDescription?: string | null;
  zoneError?: boolean;
  zoneErrorDescription?: string | null;
  productCategory?: string | null;
  riskLevel?: 'GREEN' | 'YELLOW' | 'RED' | null;
  riskExplanation?: string | null;
  productClassificationError?: boolean;
  productClassificationErrorDescription?: string | null;
  integrationSystem?: string | null;
  metadata?: Record<string, unknown> | null;
  geocodeProvider?: string | null;
  geocodeFallback?: boolean | null;
  geocodeMethod?: string | null;
  shippingLevel?: ShippingLevel | null;
}

export type ShippingLevel =
  | 'CREATED'
  | 'WAS_PROCESSED'
  | 'HAS_AN_ATTEMPT'
  | 'IS_DELIVERED';

export const cleanShippingDetails = (value?: string | null): string => {
  const trimmed = value?.trim() ?? '';
  return trimmed === 'N/A' ? '' : trimmed;
};

export type ShippingEnrichmentStep = 'timeSlot' | 'geocode' | 'zones';

export const ALL_SHIPPING_ENRICHMENT_STEPS: ShippingEnrichmentStep[] = [
  'timeSlot',
  'geocode',
  'zones',
];

export interface ShippingEnrichmentLocation {
  lat: number | null;
  long: number | null;
  zoneId: string | null;
  zoneName: string | null;
  zoneTypeId: string | null;
  zoneTypeName: string | null;
  zones: unknown[];
}

export interface ShippingEnrichmentResult {
  shippingNumber: number;
  durationMs: number;
  steps: ShippingEnrichmentStep[];
  issues: unknown[];
  geocoderError: boolean;
  zoneError: boolean;
  externalLabelError: boolean;
  destination: ShippingEnrichmentLocation;
  origin: ShippingEnrichmentLocation;
}

export interface RunShippingEnrichmentRequest {
  number: string;
  steps?: ShippingEnrichmentStep[];
  forceRegenerateLabel?: boolean;
}

export const shippingProductSchema = z.object({
  sku: z.string().optional(),
  code: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  quantity: z.coerce.number().min(1, 'La cantidad debe ser mayor a 0'),
  details: z.string().optional(),
  weight: z.coerce
    .number()
    .min(0, 'El peso debe ser un número positivo')
    .optional(),
  value: z.coerce
    .number()
    .min(0, 'El valor debe ser un número positivo')
    .optional(),
  productSizeId: z.string().optional(),
});

export const shippingApiSchema = z.object({
  number: z.string().min(1, 'El número de envío es requerido'),
  originAddressId: z.string().min(1, 'La dirección de origen es requerida'),
  destinationAddressId: z
    .string()
    .min(1, 'La dirección de destino es requerida'),
  logisticOperatorId: z.string().min(1, 'El operador logístico es requerido'),
  commerceId: z.string().min(1, 'El comercio es requerido'),
  recipient: z.string().min(1, 'El nombre del destinatario es requerido'),
  recipientPhoneNumber: z
    .string()
    .min(1, 'El teléfono del cliente es requerido'),
  dateToDelivery: z.date().optional(),
  dateDelivered: z.string().optional(),
  commerceShippingNumber: z
    .string()
    .min(1, 'El número de envío del comercio es requerido'),
  logisticOperatorShippingNumber: z.string().optional(),
  invoiceNumber: z.string().optional(),
  originCountryId: z.string().min(1, 'El país de origen es requerido'),
  originCityId: z.string().min(1, 'La ciudad de origen es requerida'),
  originStateOrDepartmentId: z
    .string()
    .min(1, 'El estado/departamento de origen es requerido'),
  originNeighborhoodId: z.string().optional(),
  destinationCountryId: z.string().min(1, 'El país de destino es requerido'),
  destinationCityId: z.string().min(1, 'La ciudad de destino es requerida'),
  destinationStateOrDepartmentId: z
    .string()
    .min(1, 'El estado/departamento de destino es requerido'),
  destinationNeighborhoodId: z.string().optional(),
  destinationStreetName: z
    .string()
    .min(1, 'El nombre de la calle es requerido'),
  destinationDoorNumber: z.string().min(1, 'El número de puerta es requerido'),
  destinationApartmentNumber: z.string().optional(),
  destinationCorner: z.string().optional(),
  originStreetName: z
    .string()
    .min(1, 'El nombre de la calle de origen es requerido'),
  originDoorNumber: z
    .string()
    .min(1, 'El número de puerta de origen es requerido'),
  originApartmentNumber: z.string().optional(),
  originCorner: z.string().optional(),
  administrationDetails: z.string().optional(),
  deliveryDetails: z.string().optional(),
  pickupDetails: z.string().min(1, 'Los detalles de recogida son requeridos'),
  statusId: z.string().min(1, 'El estado es requerido'),
  sizeId: z.string().min(1, 'El tamaño es requerido'),
  classificationId: z.string().optional(),
  complexityId: z.string().min(1, 'La complejidad es requerida'),
  typeId: z.string().optional(),
  tags: z.string().optional(),
  channelId: z.string().min(1, 'El canal es requerido'),
  branchId: z.string().min(1, 'La sucursal es requerida'),
  timeSlotId: z.string().min(1, 'El horario es requerido'),
  timeFrom: z.date().optional(),
  timeTo: z.date().optional(),
  commercialServiceId: z.string().min(1, 'El servicio comercial es requerido'),
  entryDate: z.string().optional(),
  acceptedDate: z.string().optional(),
  labeledDate: z.string().optional(),
  modifiedDate: z.string().optional(),
  pickedUpDate: z.string().optional(),
  products: z.array(shippingProductSchema).optional(),
});

const shippingObjectSchema = z.object({
  logisticOperatorId: z.string().min(1, 'El operador logístico es requerido'),
  commerceId: z.string().min(1, 'El comercio es requerido'),
  branchId: z.string().min(1, 'La sucursal es requerida'),
  commercialServiceId: z.string().optional(),
  recipient: z.string().optional().default(''),
  recipientPhoneNumber: z.string().optional().default(''),
  recipientId: z
    .string()
    .regex(/^\d{0,20}$/, 'El ID de destinatario debe ser un número')
    .optional()
    .default(''),
  serviceIsPickup: z.boolean().optional().default(false),
  dateToDelivery: z.date().optional(),

  originCountryId: requiredUuid('El país de origen es requerido'),
  originCityId: requiredUuid('La ciudad de origen es requerida'),
  originStateOrDepartmentId: requiredUuid(
    'El estado/departamento de origen es requerido'
  ),
  originNeighborhoodId: z.string().optional(),
  originStreetName: z
    .string()
    .min(1, 'El nombre de la calle de origen es requerido'),
  originDoorNumber: z
    .string()
    .min(1, 'El número de puerta de origen es requerido'),
  originApartmentNumber: z.string().optional(),
  originCorner: z.string().optional(),
  originLat: z.string().optional(),
  originLong: z.string().optional(),

  destinationCountryId: z.string().optional().default(''),
  destinationCityId: requiredUuid('La ciudad de destino es requerida'),
  destinationStateOrDepartmentId: requiredUuid(
    'El estado/departamento de destino es requerido'
  ),
  destinationNeighborhoodId: z.string().optional(),
  destinationStreetName: z
    .string()
    .min(1, 'El nombre de la calle es requerido'),
  destinationDoorNumber: z.string().min(1, 'El número de puerta es requerido'),
  destinationApartmentNumber: z.string().optional(),
  destinationCorner: z.string().optional(),
  destinationZipCode: z.string().optional(),
  destinationLat: z.string().optional(),
  destinationLong: z.string().optional(),

  administrationDetails: z.string().optional(),
  deliveryDetails: z.string().optional(),
  pickupDetails: z.string().optional(),
  invoiceNumber: z.string().optional(),
  products: z.preprocess(
    (val) =>
      Array.isArray(val) ? val.filter((row) => !isEmptyProductRow(row)) : val,
    z.array(shippingProductSchema).optional()
  ),

  number: z.string().optional(),
  originAddressId: z.string().optional(),
  destinationAddressId: z.string().optional(),
  commerceShippingNumber: z.string().optional(),
  logisticOperatorShippingNumber: z.string().optional(),
  packagesQuantity: packagesQuantitySchema,
  dateDelivered: z.string().optional(),
  statusId: z.string().optional(),
  sizeId: z.string().optional(),
  classificationId: z.string().optional(),
  complexityId: z.string().optional(),
  typeId: z.string().optional(),
  tags: z.string().optional(),
  channelId: z.string().optional(),
  timeSlotId: z.string().optional().default(''),
  timeFrom: z.date().optional(),
  timeTo: z.date().optional(),
  entryDate: z.string().optional(),
  acceptedDate: z.string().optional(),
  labeledDate: z.string().optional(),
  modifiedDate: z.string().optional(),
  pickedUpDate: z.string().optional(),
  serviceWithOperatorId: z
    .string()
    .min(1, 'El servicio del operador es requerido'),
});

const refineShippingSchema = (
  data: z.infer<typeof shippingObjectSchema>,
  ctx: z.RefinementCtx,
  options: { requireTimeSlot?: boolean; requireProductSize?: boolean } = {}
) => {
  const { requireTimeSlot = true, requireProductSize = false } = options;

  if (requireProductSize && Array.isArray(data.products)) {
    data.products.forEach((product, index, products) => {
      const hasData = Boolean(
        product?.name?.trim() || product?.sku?.trim() || product?.code?.trim()
      );
      if (hasData && !product?.name?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Ingresá el nombre del producto',
          path: ['products', index, 'name'],
        });
      }
      if (
        hasData &&
        (!product?.productSizeId || product.productSizeId === '')
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            products.length > 1
              ? `Seleccioná el tamaño del producto ${index + 1}`
              : 'Seleccioná el tamaño del producto',
          path: ['products', index, 'productSizeId'],
        });
      }
    });
  }

  if (
    requireTimeSlot &&
    !data.serviceIsPickup &&
    (!data.timeSlotId || data.timeSlotId === '')
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Debe seleccionar una franja horaria',
      path: ['timeSlotId'],
    });
  }

  if (data.timeSlotId === 'special') {
    if (!data.timeFrom) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Debe indicar la hora de inicio',
        path: ['timeFrom'],
      });
    }
    if (!data.timeTo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Debe indicar la hora de fin',
        path: ['timeTo'],
      });
    }
  }

  if (!data.serviceIsPickup) {
    if (!data.recipient || data.recipient.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'El nombre del destinatario es requerido',
        path: ['recipient'],
      });
    }
  }
};

export const shippingSchema = shippingObjectSchema.superRefine((data, ctx) =>
  refineShippingSchema(data, ctx, {
    requireTimeSlot: true,
    requireProductSize: true,
  })
);

export const getShippingValidationIssues = (
  values: unknown,
  options: { requireTimeSlot?: boolean; requireProductSize?: boolean } = {
    requireTimeSlot: true,
  }
): z.ZodIssue[] => {
  const base = shippingObjectSchema.safeParse(values);
  const refined = z
    .any()
    .superRefine((data, ctx) => refineShippingSchema(data ?? {}, ctx, options))
    .safeParse(values);
  return [
    ...(base.success ? [] : base.error.issues),
    ...(refined.success ? [] : refined.error.issues),
  ];
};

/**
 * Variante para /business-commerces: la franja horaria no es obligatoria.
 */
export const commerceShippingSchema = shippingObjectSchema.superRefine(
  (data, ctx) =>
    refineShippingSchema(data, ctx, {
      requireTimeSlot: false,
      requireProductSize: true,
    })
);

export type ShippingSchema = z.infer<typeof shippingSchema>;
export type ShippingProductSchema = z.infer<typeof shippingProductSchema>;

export const defaultShippingValues: ShippingSchema = {
  number: '',
  originAddressId: '',
  destinationAddressId: '',
  logisticOperatorId: '',
  commerceId: '',
  recipient: '',
  recipientPhoneNumber: '',
  recipientId: '',
  serviceIsPickup: false,
  dateToDelivery: new Date(),
  timeSlotId: '',
  timeFrom: new Date(),
  timeTo: new Date(),
  dateDelivered: '',
  commerceShippingNumber: '',
  logisticOperatorShippingNumber: '',
  packagesQuantity: '',
  invoiceNumber: '',
  originCountryId: '',
  originCityId: '',
  originStateOrDepartmentId: '',
  originNeighborhoodId: '',
  destinationCountryId: '',
  destinationCityId: '',
  destinationStateOrDepartmentId: '',
  destinationNeighborhoodId: '',
  destinationStreetName: '',
  destinationDoorNumber: '',
  destinationApartmentNumber: '',
  destinationCorner: '',
  destinationZipCode: '',
  originStreetName: '',
  originDoorNumber: '',
  originApartmentNumber: '',
  originCorner: '',
  administrationDetails: '',
  deliveryDetails: '',
  pickupDetails: '',
  statusId: '',
  sizeId: '',
  classificationId: '',
  complexityId: '',
  typeId: '',
  tags: '',
  channelId: '',
  branchId: '',
  commercialServiceId: '',
  serviceWithOperatorId: '',
  entryDate: '',
  acceptedDate: '',
  labeledDate: '',
  modifiedDate: '',
  pickedUpDate: '',
  products: [
    {
      sku: '',
      code: '',
      name: '',
      description: '',
      quantity: 1,
      details: '',
      weight: 0,
      value: 0,
      productSizeId: '',
    },
  ],
};

export const DEFAULT_GEOGRAPHIC_CONFIG = {
  defaultCountryName: 'Uruguay',
  defaultDepartmentName: 'Montevideo',
  defaultCityName: 'Montevideo',
} as const;

export const defaultShippingProductValues: ShippingProductSchema = {
  sku: '',
  code: '',
  name: '',
  description: '',
  quantity: 1,
  details: '',
  weight: 0,
  value: 0,
  productSizeId: '',
};

export interface ShippingsFilter {
  commerceId?: string;
  logisticOperatorId?: string;
  driverId?: string;
  number?: string;
  commerceShippingNumber?: string;
  labelNumber?: string;
  recipient?: string;
  recipientPhoneNumber?: string;
  destinationAddress?: string;
  driverName?: string;
  driverPhoneNumber?: string;
  originAddress?: string;
  cityIds?: string[];
  cityNames?: string[];
  destinationStateOrDepartmentIds?: string[];
  destinationStateOrDepartmentName?: string;
  originStateOrDepartmentIds?: string[];
  originStateOrDepartmentName?: string;
  timeSlotIds?: string[];
  branchId?: string;
  branch?: string;
  tags?: string;
  serviceWithOperatorIds?: string[];
  statusId?: string;
  deliveryDateFrom?: Date | null;
  deliveryDateTo?: Date | null;
  dateDeliveredFrom?: Date | null;
  dateDeliveredTo?: Date | null;
  createdAtFrom?: Date | null;
  createdAtTo?: Date | null;
  zoneIds?: string[];
  zoneNames?: string[];
  shippingLevels?: string[];
}
